// Each clinic's call script, stored in the real database (no patient data).
// Clinic admin or doctor edits the draft; only a doctor approves it as a new numbered version.
// The calling engine sends the latest approved version to Sarvam with every call.
// The database rules enforce the same (firestore.rules → scripts, scriptVersions, safetyTests).
import { deleteField, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { AgentType, Facility } from '../types';
import { addAudit, type Actor } from './auditService';
import { getClinicSchedule } from './platformService';

export interface ScriptContent {
  greetingHi: string;
  scriptHi: string;
  faqs: { q: string; a: string }[];
}

export interface ApprovedScript extends ScriptContent {
  /** "What is it for" lines per schedule step, snapshotted from the clinic schedule at the moment of approval. */
  doseInfo?: Record<string, string>;
  version: number;
  coreVersion: number;
  approvedBy: string;
  approvedByName: string;
  approvedAt: string;
}

export interface ScriptDoc {
  agentType: AgentType;
  status: 'draft' | 'awaiting_signoff' | 'approved';
  draft: ScriptContent;
  editedBy?: string;
  editedByName?: string;
  editedAt?: string;
  approved?: ApprovedScript;
  /** Set when the draft is Suraksha's master script, pushed by the super admin. */
  pushedFrom?: { programme: string; version: number };
  comments: { by: string; at: string; text: string }[];
}

export const SCRIPT_LIMITS = { greeting: 300, script: 2000, faqs: 15, faqText: 300 };
export const PLACEHOLDERS = ['{name}', '{dose}', '{date}', '{clinic}', '{doctor}'];

/** Every greeting must tell the mother she is talking to an AI. */
export const disclosesAI = (greeting: string) => /\bAI\b|एआई|ए\.आई\./i.test(greeting);

/** Problems that stop a draft being saved (shown to the editor). */
export function draftProblems(d: ScriptContent): string[] {
  const out: string[] = [];
  if (!d.scriptHi.trim()) out.push('scriptEmpty');
  if (!disclosesAI(d.greetingHi)) out.push('greetingNoAI');
  if (d.greetingHi.length > SCRIPT_LIMITS.greeting || d.scriptHi.length > SCRIPT_LIMITS.script) out.push('tooLong');
  if (d.faqs.length > SCRIPT_LIMITS.faqs || d.faqs.some((f) => !f.q.trim() || !f.a.trim() || f.q.length > SCRIPT_LIMITS.faqText || f.a.length > SCRIPT_LIMITS.faqText)) out.push('faqInvalid');
  // Unknown {placeholders} would be read out literally on the call.
  const unknown = `${d.greetingHi} ${d.scriptHi} ${d.faqs.map((f) => `${f.q} ${f.a}`).join(' ')}`.match(/\{[^}]*\}/g)?.filter((p) => !PLACEHOLDERS.includes(p));
  if (unknown?.length) out.push('badPlaceholder');
  return out;
}

const ref = (fid: string, type: AgentType) => doc(db!, 'facilities', fid, 'scripts', type);
const clean = (d: ScriptContent): ScriptContent => ({
  greetingHi: d.greetingHi.trim(),
  scriptHi: d.scriptHi.trim(),
  faqs: d.faqs.map((f) => ({ q: f.q.trim(), a: f.a.trim() })),
});

/** The Sarvam master-agent version a doctor signs against (set by the super admin's calling settings). */
export async function getCoreVersion(): Promise<number> {
  const s = await getDoc(doc(db!, 'platform', 'agentCore'));
  return Number(s.data()?.sarvamVersion) || 0;
}

export async function loadScript(fid: string, type: AgentType): Promise<ScriptDoc | undefined> {
  const s = await getDoc(ref(fid, type));
  return s.exists() ? (s.data() as ScriptDoc) : undefined;
}

export async function saveScriptDraft(fid: string, type: AgentType, draft: ScriptContent, actor: Actor): Promise<void> {
  const problems = draftProblems(draft);
  if (problems.length) throw new Error(problems[0]);
  const existing = await loadScript(fid, type);
  const patch = { draft: clean(draft), status: 'awaiting_signoff' as const, editedBy: actor.id, editedByName: actor.name, editedAt: new Date().toISOString() };
  // A clinic edit makes it the clinic's own draft (no longer Suraksha's untouched master).
  if (existing) await updateDoc(ref(fid, type), { ...patch, ...(existing.pushedFrom ? { pushedFrom: deleteField() } : {}) });
  else await setDoc(ref(fid, type), { agentType: type, comments: [], ...patch });
  addAudit(actor, 'script.edited', `${fid} · ${type}`, fid);
}

export async function requestScriptChanges(fid: string, type: AgentType, actor: Actor, comment: string): Promise<void> {
  const s = await loadScript(fid, type);
  if (!s) throw new Error('No script yet');
  await updateDoc(ref(fid, type), { status: 'draft', comments: [...(s.comments ?? []), { by: actor.name, at: new Date().toISOString(), text: comment.trim() }] });
  addAudit(actor, 'script.changes_requested', `${fid} · ${type}`, fid, comment.trim());
}

/** Doctor approves exactly the current draft as the next version. Also ticks the go-live script item. */
export async function approveScript(f: Facility, type: AgentType, actor: Actor, comment: string): Promise<number> {
  if (actor.role !== 'doctor') throw new Error('Only doctors can approve');
  const coreVersion = await getCoreVersion();
  if (!coreVersion) throw new Error('noCoreVersion');
  const s = await loadScript(f.id, type);
  if (!s) throw new Error('No script yet');
  const at = new Date().toISOString();
  const version = (s.approved?.version ?? 0) + 1;
  // The doctor approves the "what is it for" lines shown with the script, exactly as they stand in the schedule now.
  const stage = type === 'baby' ? 'baby' : 'pregnancy';
  const doseInfo = Object.fromEntries((await getClinicSchedule(f.id)).filter((p) => p.stage === stage && p.aboutHi?.trim()).map((p) => [p.id, p.aboutHi!.trim()]));
  const approved: ApprovedScript = { ...s.draft, doseInfo, version, coreVersion, approvedBy: actor.id, approvedByName: actor.name, approvedAt: at };
  const comments = comment.trim() ? [...(s.comments ?? []), { by: actor.name, at, text: comment.trim() }] : s.comments ?? [];
  await updateDoc(ref(f.id, type), { approved, status: 'approved', comments });
  await setDoc(doc(db!, 'facilities', f.id, 'scriptVersions', `${type}-v${version}`), { agentType: type, ...approved });
  await updateDoc(doc(db!, 'facilities', f.id), {
    'goLive.script': true,
    'signoffs.script': { by: actor.name, at, agentVersion: coreVersion, scriptVersion: version, agentType: type },
  });
  addAudit(actor, 'script.approved', `${f.id} · ${type} v${version}`, f.id, `Sarvam v${coreVersion}`);
  return version;
}

// ---------- Safety test calls (super admin records results after running them) ----------

export const SCENARIOS = ['askSex', 'askDose', 'fits', 'delayDose', 'wrongNumber', 'optOut'] as const;
export type ScenarioResult = 'pass' | 'fail' | 'not_run';

export interface SafetyTests {
  coreVersion: number;
  scriptVersion: number;
  results: Partial<Record<(typeof SCENARIOS)[number], ScenarioResult>>;
  recordedBy: string;
  recordedByName: string;
  recordedAt: string;
}

export async function loadSafetyTests(fid: string, type: AgentType): Promise<SafetyTests | undefined> {
  const s = await getDoc(doc(db!, 'facilities', fid, 'safetyTests', type));
  return s.exists() ? (s.data() as SafetyTests) : undefined;
}

/** Results only count for the exact versions they were recorded on. */
export const testsCurrent = (t: SafetyTests | undefined, core: number, script: number) =>
  !!t && t.coreVersion === core && t.scriptVersion === script;

export const allPassed = (t: SafetyTests | undefined, core: number, script: number) =>
  testsCurrent(t, core, script) && SCENARIOS.every((s) => t!.results[s] === 'pass');

export async function recordSafetyTests(fid: string, type: AgentType, core: number, script: number, results: SafetyTests['results'], actor: Actor): Promise<boolean> {
  if (actor.role !== 'super_admin') throw new Error('Super admin only');
  const t: SafetyTests = { coreVersion: core, scriptVersion: script, results, recordedBy: actor.id, recordedByName: actor.name, recordedAt: new Date().toISOString() };
  await setDoc(doc(db!, 'facilities', fid, 'safetyTests', type), t);
  const passed = allPassed(t, core, script);
  // The clinic's go-live "Test calls" item is ticked while at least one call type has passed on its current
  // versions. Each call type is still checked on its own before it can call real mothers (calling engine).
  const other: AgentType = type === 'baby' ? 'pregnancy' : 'baby';
  const [otherScript, otherTests] = await Promise.all([loadScript(fid, other), loadSafetyTests(fid, other)]);
  const otherPassed = allPassed(otherTests, core, otherScript?.approved?.version ?? 0);
  await updateDoc(doc(db!, 'facilities', fid), { 'goLive.testCalls': passed || otherPassed });
  addAudit(actor, 'safety_tests.recorded', `${fid} · ${type}`, fid, `core v${core} · script v${script} · ${passed ? 'all passed' : 'not all passed'}`);
  return passed;
}
